import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail,
  Bell,
  BellOff,
  Send,
  Check,
  Loader2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Settings,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import Layout from "../components/layout/Layout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [emailLog, setEmailLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [settingsRes, logRes] = await Promise.all([
        axios.get(`${API}/email-settings`),
        axios.get(`${API}/email-log`),
      ]);
      setSettings(settingsRes.data);
      setEmailLog(logRes.data);
    } catch (err) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/email-settings`, {
        enabled: settings.enabled,
        notification_email: settings.notification_email,
        preferences: settings.preferences,
      });
      setSettings(response.data);
      toast.success("Email settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    setSendingTest(true);
    try {
      const response = await axios.post(`${API}/email-settings/test`);
      toast.success(response.data.message);
      // Refresh log
      const logRes = await axios.get(`${API}/email-log`);
      setEmailLog(logRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setSendingTest(false);
    }
  };

  const updatePreference = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        [key]: value,
      },
    }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-zinc-400">Loading settings...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-3xl" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-50 font-['Outfit'] tracking-tight">
            Email Settings
          </h1>
          <p className="text-zinc-500 mt-1">
            Configure alert email notifications
          </p>
        </div>

        {/* Master Toggle */}
        <Card className="sheriff-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-md flex items-center justify-center ${settings?.enabled ? 'bg-amber-500/10' : 'bg-zinc-800'}`}>
                  {settings?.enabled ? (
                    <Bell className="w-6 h-6 text-amber-500" />
                  ) : (
                    <BellOff className="w-6 h-6 text-zinc-500" />
                  )}
                </div>
                <div>
                  <p className="text-lg font-semibold text-slate-50">
                    Email Notifications
                  </p>
                  <p className="text-sm text-zinc-500">
                    {settings?.enabled
                      ? "You will receive email alerts"
                      : "Email alerts are currently disabled"}
                  </p>
                </div>
              </div>
              <Switch
                data-testid="email-notifications-toggle"
                checked={settings?.enabled || false}
                onCheckedChange={(checked) =>
                  setSettings((prev) => ({ ...prev, enabled: checked }))
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Email Address */}
        <Card className="sheriff-card">
          <CardHeader className="border-b border-zinc-800 pb-4">
            <CardTitle className="text-lg font-semibold text-slate-50 flex items-center gap-2">
              <Mail className="w-5 h-5 text-amber-500" />
              Notification Email
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="notification_email" className="text-zinc-400">
                Send alerts to
              </Label>
              <Input
                id="notification_email"
                data-testid="notification-email-input"
                type="email"
                value={settings?.notification_email || ""}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    notification_email: e.target.value,
                  }))
                }
                placeholder="your@email.com"
                className="sheriff-input max-w-md"
              />
              <p className="text-xs text-zinc-600">
                Defaults to your Google account email if left empty
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Alert Type Preferences */}
        <Card className="sheriff-card">
          <CardHeader className="border-b border-zinc-800 pb-4">
            <CardTitle className="text-lg font-semibold text-slate-50 flex items-center gap-2">
              <Settings className="w-5 h-5 text-amber-500" />
              Alert Types
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-1">
            {/* Offline Alerts */}
            <div className="flex items-center justify-between py-4 border-b border-zinc-800">
              <div className="flex items-center gap-3">
                <XCircle className="w-5 h-5 text-red-500" />
                <div>
                  <p className="text-sm font-medium text-slate-50">Site Offline Alerts</p>
                  <p className="text-xs text-zinc-500">
                    Get notified when a site goes down
                  </p>
                </div>
              </div>
              <Switch
                data-testid="offline-alerts-toggle"
                checked={settings?.preferences?.offline_alerts ?? true}
                onCheckedChange={(checked) =>
                  updatePreference("offline_alerts", checked)
                }
              />
            </div>

            {/* Recovery Alerts */}
            <div className="flex items-center justify-between py-4 border-b border-zinc-800">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                <div>
                  <p className="text-sm font-medium text-slate-50">Recovery Alerts</p>
                  <p className="text-xs text-zinc-500">
                    Get notified when a site comes back online
                  </p>
                </div>
              </div>
              <Switch
                data-testid="recovery-alerts-toggle"
                checked={settings?.preferences?.recovery_alerts ?? true}
                onCheckedChange={(checked) =>
                  updatePreference("recovery_alerts", checked)
                }
              />
            </div>

            {/* Failed Check Alerts */}
            <div className="flex items-center justify-between py-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="text-sm font-medium text-slate-50">Failed Check Alerts</p>
                  <p className="text-xs text-zinc-500">
                    Get notified on monitoring check failures
                  </p>
                </div>
              </div>
              <Switch
                data-testid="failed-check-alerts-toggle"
                checked={settings?.preferences?.failed_check_alerts ?? true}
                onCheckedChange={(checked) =>
                  updatePreference("failed_check_alerts", checked)
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <Button
            data-testid="save-settings-btn"
            onClick={handleSave}
            disabled={saving}
            className="sheriff-btn-primary h-10 px-6"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            {saving ? "Saving..." : "Save Settings"}
          </Button>
          <Button
            data-testid="send-test-email-btn"
            onClick={handleSendTest}
            disabled={sendingTest}
            variant="outline"
            className="sheriff-btn-outline h-10 px-6"
          >
            {sendingTest ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            {sendingTest ? "Sending..." : "Send Test Email"}
          </Button>
        </div>

        {/* Email Log */}
        <Card className="sheriff-card">
          <CardHeader className="border-b border-zinc-800 pb-4">
            <CardTitle className="text-lg font-semibold text-slate-50 flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Recent Email Log
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {emailLog.length === 0 ? (
              <div className="p-8 text-center">
                <Mail className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500">No emails sent yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-zinc-500 font-semibold">Status</TableHead>
                    <TableHead className="text-zinc-500 font-semibold">Recipient</TableHead>
                    <TableHead className="text-zinc-500 font-semibold">Subject</TableHead>
                    <TableHead className="text-zinc-500 font-semibold">Sent At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {emailLog.slice(0, 15).map((log, idx) => (
                    <TableRow
                      key={log.log_id || idx}
                      className="border-zinc-800 hover:bg-zinc-800/50"
                    >
                      <TableCell>
                        {log.status === "sent" ? (
                          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
                            Sent
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/20">
                            Failed
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-zinc-400 text-sm">
                        {log.recipient}
                      </TableCell>
                      <TableCell className="text-slate-50 text-sm max-w-[300px] truncate">
                        {log.subject}
                      </TableCell>
                      <TableCell className="text-zinc-500 text-sm">
                        {new Date(log.sent_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default SettingsPage;
